import { useState } from 'react'
import { X, ShieldOff, Undo2 } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Modal para isentar ou desfazer isenção de comissão de defesa.
 *
 * Props:
 * - modo: 'isentar' | 'desfazer'
 * - lotesDefesa: array de lotes em defesa com { id, numero_lote, vv, cv_val, cv_pct, defesa_isenta }
 * - organizadorId: int
 * - onClose: () => void
 * - onSaved: () => void
 */
export default function IsentarDefesaModal({ modo, lotesDefesa, organizadorId, onClose, onSaved }) {
  const [saving, setSaving] = useState(false)
  const isIsentar = modo === 'isentar'

  // Filtra lotes relevantes. Usa cv_val (não cv_pct) porque no fluxo cavalo em
  // modo tabela-fixa a defesa é um valor fixo — cv_pct fica 0 propositalmente
  // (reports/mapas: _lote_defesa_dict), mas cv_val tem o valor real da comissão.
  const lotesAlvo = isIsentar
    ? lotesDefesa.filter(l => !l.defesa_isenta && l.cv_val > 0)
    : lotesDefesa.filter(l => l.defesa_isenta)

  const totalComissao = lotesAlvo.reduce((s, l) => s + (l.cv_val || 0), 0)

  const handleConfirmar = async () => {
    if (lotesAlvo.length === 0) return
    setSaving(true)
    try {
      const ids = lotesAlvo.map(l => l.id)
      if (isIsentar) {
        await lotesService.isentarDefesa(organizadorId, ids)
        toast.success(`Isenção aplicada em ${lotesAlvo.length} lote(s)`)
      } else {
        await lotesService.desfazerIsencaoDefesa(organizadorId, ids)
        toast.success(`Isenção desfeita em ${lotesAlvo.length} lote(s)`)
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isIsentar ? 'border-red-100' : 'border-gray-100'}`}>
          <div className="flex items-center gap-2">
            {isIsentar
              ? <ShieldOff className="w-5 h-5 text-red-600" />
              : <Undo2 className="w-5 h-5 text-blue-600" />
            }
            <h2 className="text-base font-semibold text-gray-800">
              {isIsentar ? 'Isentar Comissão de Defesa' : 'Desfazer Isenção de Defesa'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {isIsentar ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  Você está prestes a zerar a comissão de defesa dos seguintes lotes:
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-red-700">Lotes afetados:</span>
                  <span className="font-bold text-red-900">{lotesAlvo.length}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-red-700">Valor total da comissão que será zerada:</span>
                  <span className="font-bold text-red-900">{fmt(totalComissao)}</span>
                </div>
              </div>

              <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2">
                {lotesAlvo.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-1.5 px-2 rounded text-sm hover:bg-gray-50">
                    <span className="font-medium text-gray-800">Lote {l.numero_lote}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{l.cv_pct > 0 ? `${l.cv_pct}%` : 'Valor fixo'}</span>
                      <span className="font-semibold text-red-600">{fmt(l.cv_val)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-500">
                Esta ação pode ser desfeita a qualquer momento com o botão "Desfazer Isenção".
              </p>
            </>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  Restaurar a comissão de defesa original dos seguintes lotes:
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">Lotes a restaurar:</span>
                  <span className="font-bold text-blue-900">{lotesAlvo.length}</span>
                </div>
              </div>

              <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-100 rounded-lg p-2">
                {lotesAlvo.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-1.5 px-2 rounded text-sm hover:bg-gray-50">
                    <span className="font-medium text-gray-800">Lote {l.numero_lote}</span>
                    <span className="text-xs text-gray-400">Comissão será restaurada</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
            Cancelar
          </button>
          <button onClick={handleConfirmar} disabled={saving || lotesAlvo.length === 0}
            className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${
              isIsentar ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}>
            {saving ? 'Processando...' : isIsentar ? `Isentar ${lotesAlvo.length} lote(s)` : `Restaurar ${lotesAlvo.length} lote(s)`}
          </button>
        </div>
      </div>
    </div>
  )
}
